import { useState, useRef, useEffect, useCallback } from 'react';
import AppsIcon from '@mui/icons-material/Apps';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { Link, useNavigate } from 'react-router-dom';
import trioLogo from '@/assets/trioLogo.png';
import { useOrganization } from '@/contexts/OrganizationContext';
import UserMenuDropDown from '@/trio-ui/UserMenuDropDown';
import { UserMenuOptionType } from '@/trio-ui/UserMenuDropDown/UserMenuDropDown.component';
import { LocalStorageItemKeys } from '@/types';
import { ServiceDefinition } from '@/types/routes';
import * as S from './Header.styles';

export type HeaderProps = {
  currentService: ServiceDefinition | undefined;
  accessibleServices: ServiceDefinition[];
  username: string;
  initials: string;
  picture: string;
  hideAvatar?: boolean;
  userMenuDropDownOptions: UserMenuOptionType[];
};

const getRecentIds = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(LocalStorageItemKeys.RECENT_SERVICES) || '[]');
  } catch {
    return [];
  }
};

const saveRecent = (id: string): void => {
  try {
    const r = getRecentIds().filter((x) => x !== id);
    r.unshift(id);
    localStorage.setItem(LocalStorageItemKeys.RECENT_SERVICES, JSON.stringify(r.slice(0, 5)));
  } catch {
    /* noop */
  }
};

const HeaderComponent = function ({
  currentService,
  accessibleServices,
  username,
  initials,
  picture,
  hideAvatar,
  userMenuDropDownOptions,
}: HeaderProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { activeOrg } = useOrganization();

  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const close = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 60);
    }
  }, [isOpen]);

  useEffect((): (() => void) | void => {
    if (!isOpen) return undefined;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  const handleServiceClick = useCallback(
    (svc: ServiceDefinition) => {
      saveRecent(svc.id);
      navigate(svc.landingPath || svc.sidebarMenus[0]?.path || svc.basePath);
      close();
    },
    [navigate, close]
  );

  const filtered = search
    ? accessibleServices.filter(
        (s) =>
          s.name.toLowerCase().includes(search.toLowerCase()) ||
          s.description.toLowerCase().includes(search.toLowerCase())
      )
    : accessibleServices;

  const recentIds = getRecentIds();
  const recentServices = recentIds
    .map((id) => accessibleServices.find((s) => s.id === id))
    .filter(Boolean)
    .slice(0, 3) as ServiceDefinition[];

  const CurrentIcon = currentService?.icon;

  return (
    <>
      <S.Header>
        <S.HeaderContent>
          <S.LeftSection>
            <Link className="flex align-center" to="/home">
              <S.TrioLogo src={trioLogo} alt="Trio's logo" />
            </Link>

            {activeOrg && <S.OrgLabel>{activeOrg.name}</S.OrgLabel>}

            <S.ServicesButton type="button" onClick={toggle} $isOpen={isOpen}>
              <AppsIcon />
              <span>Services</span>
              <KeyboardArrowDownIcon />
            </S.ServicesButton>

            {currentService && (
              <>
                <S.Divider />
                <S.CurrentServiceBreadcrumb>
                  {CurrentIcon && <CurrentIcon />}
                  <span>{currentService.name}</span>
                </S.CurrentServiceBreadcrumb>
              </>
            )}
          </S.LeftSection>

          {!hideAvatar && (
            <UserMenuDropDown
              options={userMenuDropDownOptions}
              username={username}
              initials={initials}
              picture={picture}
            />
          )}
        </S.HeaderContent>
      </S.Header>

      {isOpen && (
        <>
          <S.DropdownBackdrop onClick={close} />
          <S.DropdownPanel>
            <S.DropdownInner>
              <S.DropdownSearchRow>
                <SearchIcon />
                <S.DropdownSearchInput
                  ref={searchRef}
                  type="text"
                  placeholder="Search services..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </S.DropdownSearchRow>

              {recentServices.length > 0 && !search && (
                <S.RecentSection>
                  <S.DropdownSectionLabel>Recently Visited</S.DropdownSectionLabel>
                  <S.DropdownGrid>
                    {recentServices.map((svc) => {
                      const Icon = svc.icon;
                      return (
                        <S.DropdownServiceCard
                          key={`recent-${svc.id}`}
                          href={svc.landingPath || svc.sidebarMenus[0]?.path || svc.basePath}
                          onClick={(e) => {
                            e.preventDefault();
                            handleServiceClick(svc);
                          }}
                        >
                          <S.DropdownServiceIcon $color={svc.color}>
                            <Icon />
                          </S.DropdownServiceIcon>
                          <S.DropdownServiceInfo>
                            <S.DropdownServiceName>{svc.name}</S.DropdownServiceName>
                            <S.DropdownServiceDesc>{svc.description}</S.DropdownServiceDesc>
                          </S.DropdownServiceInfo>
                        </S.DropdownServiceCard>
                      );
                    })}
                  </S.DropdownGrid>
                </S.RecentSection>
              )}

              <div>
                <S.DropdownSectionLabel>
                  {search ? 'Results' : 'All Services'}
                </S.DropdownSectionLabel>
                {filtered.length > 0 ? (
                  <S.DropdownGrid>
                    {filtered.map((svc) => {
                      const Icon = svc.icon;
                      return (
                        <S.DropdownServiceCard
                          key={svc.id}
                          href={svc.landingPath || svc.sidebarMenus[0]?.path || svc.basePath}
                          onClick={(e) => {
                            e.preventDefault();
                            handleServiceClick(svc);
                          }}
                        >
                          <S.DropdownServiceIcon $color={svc.color}>
                            <Icon />
                          </S.DropdownServiceIcon>
                          <S.DropdownServiceInfo>
                            <S.DropdownServiceName>{svc.name}</S.DropdownServiceName>
                            <S.DropdownServiceDesc>{svc.description}</S.DropdownServiceDesc>
                          </S.DropdownServiceInfo>
                        </S.DropdownServiceCard>
                      );
                    })}
                  </S.DropdownGrid>
                ) : (
                  <S.DropdownEmpty>No services found</S.DropdownEmpty>
                )}
              </div>
            </S.DropdownInner>
          </S.DropdownPanel>
        </>
      )}
    </>
  );
};

export default HeaderComponent;
